// Accessible modal dialog (accessibility.md §8 — focus trap + return focus,
// Escape to close). Bottom sheet on a phone, centered card from `sm` up
// (responsive-design.md §15).
//
// Long content (e.g. a school's full subject catalog) scrolls INSIDE the
// panel: the panel is capped at a share of the viewport and its body is the
// only scroll area, so the title + close button stay reachable no matter how
// many rows there are. Heights use `dvh`, never `vh` — responsive-design.md
// §18 (iOS Safari miscalculates `vh` as the URL bar collapses).
//
// PRD-60 chunk 0: tokens (overlay, surface, hairline, card radius, float
// shadow) and a 44px close target on a phone — it was 36px.
//
// PRD-60 chunk B, focus:
// - The effect depends on `open` only. It used to list `onClose`, which
//   every page passes inline, so each parent re-render (a mutation going
//   pending) tore the trap down, "returned" focus to the row behind the
//   overlay and set it up again — focus left the dialog mid-confirm.
// - First focus goes to the first field of the BODY, not to the close
//   button that happens to come first in the DOM.
// - Dialogs stack. Only the topmost one answers Escape and traps Tab, so
//   Escape in "Crear materia" no longer closes the Materias panel under it.
// - When the element that opened the dialog is gone (the row it acted on
//   was moved to the trash), focus lands on the page heading, not <body>.

import { cn } from "@lib/cn"
import { X } from "lucide-react"
import { useEffect, useId, useRef, type ReactNode } from "react"
import { useTranslation } from "react-i18next"

// Body-scroll lock, refcounted because modals nest (the "Materias" panel
// opens its own create/edit/confirm dialogs while it is itself a modal).
// A naive per-instance lock would unlock the page when the INNER dialog
// closed, while the outer one was still open.
let scrollLockCount = 0
let previousBodyOverflow = ""
// Open dialogs, innermost last.
const dialogStack: string[] = []

function lockBodyScroll() {
  if (scrollLockCount === 0) {
    previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
  }
  scrollLockCount += 1
}

function releaseBodyScroll() {
  scrollLockCount = Math.max(0, scrollLockCount - 1)
  if (scrollLockCount === 0) {
    document.body.style.overflow = previousBodyOverflow
  }
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** The page's h1, made programmatically focusable by `PageHeader`. */
export function focusPageHeading() {
  document.querySelector<HTMLElement>("main h1")?.focus()
}

interface Props {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
  /** Actions pinned under the scrolling body. */
  footer?: ReactNode
  className?: string
}

export function Modal({ open, title, onClose, children, footer, className }: Props) {
  const { t } = useTranslation("common")
  const id = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return

    const opener = document.activeElement as HTMLElement | null
    dialogStack.push(id)
    const isTop = () => dialogStack[dialogStack.length - 1] === id

    const onKey = (e: KeyboardEvent) => {
      if (!isTop()) return
      if (e.key === "Escape") {
        e.preventDefault()
        onCloseRef.current()
        return
      }
      if (e.key !== "Tab") return
      const focusables = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE)
      if (!focusables || focusables.length === 0) return
      const first = focusables[0]!
      const last = focusables[focusables.length - 1]!
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener("keydown", onKey)
    lockBodyScroll()
    const target =
      panelRef.current?.querySelector<HTMLElement>("[data-autofocus]") ??
      bodyRef.current?.querySelector<HTMLElement>(FOCUSABLE) ??
      panelRef.current?.querySelector<HTMLElement>(`[data-modal-footer] ${FOCUSABLE}`) ??
      closeRef.current
    target?.focus()

    return () => {
      document.removeEventListener("keydown", onKey)
      dialogStack.splice(dialogStack.lastIndexOf(id), 1)
      releaseBodyScroll()
      if (opener?.isConnected && opener !== document.body) opener.focus()
      else focusPageHeading()
    }
  }, [open, id])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center sm:items-center sm:p-4">
      <button
        type="button"
        aria-label={t("actions.close")}
        className="bg-overlay absolute inset-0 backdrop-blur-sm"
        onClick={() => onCloseRef.current()}
        tabIndex={-1}
      />
      {/* `flex flex-col` + a capped height turn the body into the single
          scroll area; the header stays pinned so "close" is always in reach. */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          "rounded-t-card sm:rounded-card bg-surface shadow-float relative z-10 flex max-h-[90dvh] w-full max-w-lg flex-col sm:max-h-[85dvh]",
          className,
        )}
      >
        <div className="border-hairline flex shrink-0 items-center justify-between gap-3 border-b px-5 py-3">
          <h2 className="text-ink truncate text-base font-semibold">{title}</h2>
          <button
            ref={closeRef}
            type="button"
            onClick={() => onCloseRef.current()}
            aria-label={t("actions.close")}
            className="rounded-control text-ink-subtle hover:bg-inset hover:text-ink inline-flex h-11 w-11 shrink-0 items-center justify-center transition-colors duration-150 md:h-9 md:w-9"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        {/* `min-h-0` is what actually lets a flex child shrink below its
            content height — without it `overflow-y-auto` never engages. */}
        <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto p-5">
          {children}
        </div>
        {footer && (
          <div data-modal-footer className="border-hairline shrink-0 border-t px-5 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * The action bar of a form that lives in a Modal body: Cancel and the submit
 * button, stuck to the bottom of the scroll area so a two-screen form never
 * hides its Save below the fold. Inside the `<form>`, so Enter still submits.
 * The negative margins cancel the body's `p-5` so the bar spans the panel;
 * `-bottom-5` because Chrome insets a sticky edge by the scroller's padding,
 * which left fields showing under a floating bar. One row even on a phone:
 * stacked, the two buttons took a sixth of a 375px screen.
 */
export function ModalActions({ children }: { children: ReactNode }) {
  return (
    <div className="border-hairline bg-surface sticky -bottom-5 z-10 -mx-5 mt-5 -mb-5 flex items-center justify-end gap-2 border-t px-5 py-4">
      {children}
    </div>
  )
}
